import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { debounce, throttle } from 'lodash';
import * as subscriptionApi from '../services/api/subscriptionApi';
import { listenForSubscriptionEvents } from '../services/socket/subscriptionSocketService';
import { useAuth } from './useAuth';
import {
  SubscriptionPlan,
  SubscriptionTier,
  GSTDetails,
  PlansResponse,
  SubscriptionsResponse,
  ActiveSubscriptionResponse,
  PaymentsResponse,
  UserSubscription
} from '../types/subscription.types';

// Cache keys for React Query
const SUBSCRIPTION_QUERY_KEYS = {
  PLANS: 'subscription-plans',
  USER_SUBSCRIPTIONS: 'user-subscriptions',
  ACTIVE_SUBSCRIPTION: 'active-subscription',
  PAYMENT_HISTORY: 'payment-history'
};

// Track the last time a refresh was performed to prevent excessive refreshes
let lastRefreshTime = 0;
const REFRESH_THROTTLE_MS = 10000; // 10 seconds between refreshes

// Parameter to store payment session data
const PAYMENT_SESSION_STORAGE_KEY = 'speedmeet_payment_session';

// Constant for unlimited passes
const UNLIMITED_PASSES_VALUE = 999999;

/**
 * Custom hook for subscription functionality
 */
export const useSubscription = () => {
  const queryClient = useQueryClient();
  const { refreshUserData, isAuthenticated } = useAuth();
  const refreshInProgressRef = useRef(false);
  
  // Create a throttled version of refreshUserData
  const throttledRefreshUserData = useCallback(
    throttle(() => {
      // Check if refresh is already in progress
      if (refreshInProgressRef.current) {
        console.log('Refresh already in progress, skipping');
        return;
      }
      
      const now = Date.now();
      if (now - lastRefreshTime >= REFRESH_THROTTLE_MS) {
        lastRefreshTime = now;
        refreshInProgressRef.current = true;
        
        refreshUserData().finally(() => {
          // Mark refresh as complete after a short delay
          setTimeout(() => {
            refreshInProgressRef.current = false;
          }, 2000);
        });
      } else {
        console.log('Skipping refreshUserData, called too frequently');
      }
    }, REFRESH_THROTTLE_MS, { leading: true, trailing: false }),
    [refreshUserData]
  );
  
  // Check for successful payment return
  useEffect(() => {
    const checkForPaymentSuccess = async () => {
      // Check if there's a payment_id and order_id in URL params (Razorpay return)
      const urlParams = new URLSearchParams(window.location.search);
      const paymentId = urlParams.get('razorpay_payment_id');
      const orderId = urlParams.get('razorpay_order_id');
      const signature = urlParams.get('razorpay_signature');
      
      // If all parameters are present, this is a payment return
      if (paymentId && orderId && signature) {
        console.log('Detected return from payment page with payment ID:', paymentId);
        
        // Get payment session data from storage
        let gstDetails: GSTDetails | undefined;
        try {
          const sessionData = localStorage.getItem(PAYMENT_SESSION_STORAGE_KEY);
          if (sessionData) {
            const parsedData = JSON.parse(sessionData);
            gstDetails = parsedData.gstDetails;
          }
        } catch (error) {
          console.error('Error retrieving payment session data:', error);
        }
        
        // Verify the payment
        try {
          toast.loading('Verifying your payment...');
          
          await verifyPaymentMutation.mutateAsync({
            razorpayOrderId: orderId,
            razorpayPaymentId: paymentId,
            razorpaySignature: signature,
            gstDetails
          });
          
          // Clear payment session data
          localStorage.removeItem(PAYMENT_SESSION_STORAGE_KEY);
          
          // Remove query parameters from URL
          const url = new URL(window.location.href);
          url.search = '';
          window.history.replaceState({}, document.title, url.toString());
          
          toast.dismiss();
          toast.success('Payment verified and subscription activated');
        } catch (error) {
          toast.dismiss();
          console.error('Error verifying payment:', error);
          toast.error('Failed to verify payment. Please contact support.');
        }
      }
    };
    
    if (isAuthenticated) {
      checkForPaymentSuccess();
    }
  }, [isAuthenticated]);
  
  // Fetch all plans - increased cache time since plans rarely change
  const {
    data: plansData,
    isLoading: isLoadingPlans,
    error: plansError
  } = useQuery<PlansResponse>({
    queryKey: [SUBSCRIPTION_QUERY_KEYS.PLANS],
    queryFn: subscriptionApi.getPlans,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    gcTime: 1000 * 60 * 60 * 48, // 48 hours (formerly cacheTime)
    refetchOnWindowFocus: false
  });
  
  // Fetch user subscriptions with increased stale time
  const {
    data: subscriptionsData,
    isLoading: isLoadingSubscriptions,
    error: subscriptionsError,
    refetch: refetchSubscriptions
  } = useQuery<SubscriptionsResponse>({
    queryKey: [SUBSCRIPTION_QUERY_KEYS.USER_SUBSCRIPTIONS],
    queryFn: subscriptionApi.getUserSubscriptions,
    staleTime: 1000 * 60 * 15, // 15 minutes
    refetchOnWindowFocus: false,
    enabled: isAuthenticated // Only fetch when authenticated
  });
  
  // Fetch active subscription with increased stale time
  const {
    data: activeSubscriptionData,
    isLoading: isLoadingActiveSubscription,
    error: activeSubscriptionError,
    refetch: refetchActiveSubscription
  } = useQuery<ActiveSubscriptionResponse>({
    queryKey: [SUBSCRIPTION_QUERY_KEYS.ACTIVE_SUBSCRIPTION],
    queryFn: subscriptionApi.getActiveSubscription,
    staleTime: 1000 * 60 * 15, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: isAuthenticated // Only fetch when authenticated
  });
  
  // Fetch payment history - lazy loading
  const {
    data: paymentsData,
    isLoading: isLoadingPayments,
    error: paymentsError,
    refetch: refetchPayments
  } = useQuery<PaymentsResponse>({
    queryKey: [SUBSCRIPTION_QUERY_KEYS.PAYMENT_HISTORY],
    queryFn: () => subscriptionApi.getPaymentHistory(20),
    staleTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
    enabled: false // Only fetch when needed
  });
  
  // Set active subscription mutation
  const setActiveMutation = useMutation({
    mutationFn: (subscriptionId: string) => subscriptionApi.setActiveSubscription(subscriptionId),
    onSuccess: () => {
      toast.success('Subscription activated successfully');
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEYS.ACTIVE_SUBSCRIPTION] });
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEYS.USER_SUBSCRIPTIONS] });
      // Refresh user data to update meeting passes (using throttled version)
      throttledRefreshUserData();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to activate subscription');
    }
  });
  
  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: (params: { planCode: string; gstDetails?: GSTDetails; useDirectPaymentLink?: boolean }) => 
      subscriptionApi.createOrder(
        params.planCode, 
        params.gstDetails,
        params.useDirectPaymentLink !== undefined ? params.useDirectPaymentLink : true
      ),
    onError: (error: any) => {
      console.error('Create order error:', error);
      toast.error(error.response?.data?.message || 'Failed to create order');
    }
  });
  
  // Verify payment mutation
  const verifyPaymentMutation = useMutation({
    mutationFn: (params: { 
      razorpayOrderId: string; 
      razorpayPaymentId: string; 
      razorpaySignature: string; 
      gstDetails?: GSTDetails 
    }) => 
      subscriptionApi.verifyPayment(
        params.razorpayOrderId, 
        params.razorpayPaymentId, 
        params.razorpaySignature, 
        params.gstDetails
      ),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEYS.ACTIVE_SUBSCRIPTION] });
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEYS.USER_SUBSCRIPTIONS] });
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEYS.PAYMENT_HISTORY] });
      // Refresh user data to update meeting passes (using throttled version)
      throttledRefreshUserData();
    },
    onError: (error: any) => {
      console.error('Payment verification error:', error);
      toast.error(error.response?.data?.message || 'Failed to verify payment');
    }
  });
  
  // Save GST details mutation
  const saveGSTDetailsMutation = useMutation({
    mutationFn: (gstDetails: GSTDetails) => subscriptionApi.saveGSTDetails(gstDetails),
    onSuccess: () => {
      toast.success('GST details saved successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save GST details');
    }
  });
  
  // Extract and memoize plans data
  const plans = useMemo(() => (plansData ? plansData.plans : []), [plansData]);
  
  // Extract and memoize subscriptions data
  const subscriptions = useMemo(() => (subscriptionsData ? subscriptionsData.subscriptions : []), [subscriptionsData]);
  const activeSubscriptionId = useMemo(() => subscriptionsData?.activeSubscriptionId, [subscriptionsData]);
  
  // Extract and memoize active subscription
  const activeSubscription = useMemo(() => (activeSubscriptionData ? activeSubscriptionData.subscription : undefined), [activeSubscriptionData]);
  
  // Extract and memoize payments data
  const payments = useMemo(() => (paymentsData ? paymentsData.payments : []), [paymentsData]);
  
  // Create debounced versions of refresh functions
  const debouncedRefetchActive = useCallback(debounce(() => {
    refetchActiveSubscription();
  }, 1000), [refetchActiveSubscription]);
  
  const debouncedRefetchSubscriptions = useCallback(debounce(() => {
    refetchSubscriptions();
  }, 1000), [refetchSubscriptions]);
  
  const debouncedRefetchPayments = useCallback(debounce(() => {
    refetchPayments();
  }, 1000), [refetchPayments]);
  
  // Socket event handlers with debouncing to prevent rapid calls
  useEffect(() => {
    // Don't set up socket event handlers if not authenticated
    if (!isAuthenticated) {
      return () => {};
    }
    
    const cleanup = listenForSubscriptionEvents(
      // Active subscription changed
      () => {
        debouncedRefetchActive();
        throttledRefreshUserData();
      },
      // New subscription purchased
      () => {
        debouncedRefetchActive();
        debouncedRefetchSubscriptions();
        debouncedRefetchPayments();
        throttledRefreshUserData();
        toast.success('New subscription purchased successfully');
      },
      // Subscription expired
      () => {
        debouncedRefetchActive();
        debouncedRefetchSubscriptions();
        throttledRefreshUserData();
        toast('A subscription has expired');
      },
      // Meeting passes updated
      () => {
        debouncedRefetchActive();
        // Set a delay to avoid race conditions with other updates
        setTimeout(() => {
          throttledRefreshUserData();
        }, 1000);
      }
    );
    
    return cleanup;
  }, [
    isAuthenticated,
    debouncedRefetchActive,
    debouncedRefetchSubscriptions,
    debouncedRefetchPayments,
    throttledRefreshUserData
  ]);
  
  // Set active subscription
  const setActiveSubscription = useCallback((subscriptionId: string) => {
    setActiveMutation.mutate(subscriptionId);
  }, [setActiveMutation]);
  
  // Find plan by code
  const getPlanByCode = useCallback((code: string): SubscriptionPlan | undefined => {
    return plans.find((plan: SubscriptionPlan) => plan.code === code.toUpperCase());
  }, [plans]);
  
  // Format currency
  const formatCurrency = useCallback((amount: number, currency: string = 'INR'): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(amount);
  }, []);
  
  // Calculate price without GST (assuming 18% GST)
  const calculatePreGSTPrice = useCallback((priceWithGST: number): number => {
    // Formula: original price = price with GST / (1 + GST rate)
    // For 18% GST: original price = price with GST / 1.18
    return Math.round(priceWithGST / 1.18);
  }, []);

  // Format currency without GST
  const formatCurrencyWithoutGST = useCallback((amountWithGST: number, currency: string = 'INR'): string => {
    const amountWithoutGST = calculatePreGSTPrice(amountWithGST);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(amountWithoutGST);
  }, [calculatePreGSTPrice]);

  // Calculate price per pass without GST
  const calculatePricePerPassWithoutGST = useCallback((plan: SubscriptionPlan): string => {
    if (plan.price === 0 || plan.passes === 0) return 'Free';
    if (plan.isUnlimited || plan.passes >= 999999) return formatCurrencyWithoutGST(plan.price);
    
    const priceWithoutGST = calculatePreGSTPrice(plan.price);
    return formatCurrency(Math.round(priceWithoutGST / plan.passes));
  }, [calculatePreGSTPrice, formatCurrency, formatCurrencyWithoutGST]);
  
  // Format date
  const formatDate = useCallback((dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);
  
  // Get remaining days for subscription
  const getRemainingDays = useCallback((expiryDate: string): number => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, []);
  
  // Function to log payment debugging information
  const logPaymentDebug = useCallback((info: {
    action: string;
    success: boolean;
    data?: any;
    error?: any;
  }) => {
    try {
      // Store debug info in localStorage
      const debugData = {
        ...info,
        timestamp: new Date().toISOString()
      };
      
      // Don't log sensitive information
      if (debugData.data) {
        // Clean sensitive data
        if (debugData.data.razorpaySignature) {
          debugData.data.razorpaySignature = '[REDACTED]';
        }
      }
      
      // Get existing debug info
      const existingDebugJson = localStorage.getItem('speedmeet_payment_debug');
      let existingDebug = existingDebugJson ? JSON.parse(existingDebugJson) : { logs: [] };
      
      // Add new entry, keeping only the latest 10
      existingDebug.logs = [debugData, ...(existingDebug.logs || [])].slice(0, 10);
      
      localStorage.setItem('speedmeet_payment_debug', JSON.stringify(existingDebug));
    } catch (e) {
      // Ignore localStorage errors
      console.warn('Failed to log payment debug info', e);
    }
  }, []);
  
  // Create and open Razorpay checkout with direct payment link
  const openRazorpayCheckout = useCallback(async (
    planCode: string, 
    gstDetails?: GSTDetails
  ): Promise<boolean> => {
    try {
      // Create order
      console.log('Creating order for plan:', planCode);
      const orderResponse = await createOrderMutation.mutateAsync({
        planCode,
        gstDetails,
        useDirectPaymentLink: true
      });
      
      // Check if order creation was successful
      if (!orderResponse.success || !orderResponse.orderId) {
        throw new Error('Failed to create order: ' + (orderResponse.message || 'Unknown error'));
      }
      
      console.log('Order created successfully:', orderResponse.orderId);
      
      // Check if we have a payment URL in the response
      if (!orderResponse.paymentUrl) {
        throw new Error('No payment URL received from server');
      }
      
      // Save session data in localStorage to retrieve after payment return
      try {
        const sessionData = {
          orderId: orderResponse.orderId,
          planCode,
          gstDetails,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem(PAYMENT_SESSION_STORAGE_KEY, JSON.stringify(sessionData));
      } catch (error) {
        console.warn('Failed to save payment session data:', error);
      }
      
      // Log debug information
      logPaymentDebug({
        action: 'redirect_to_payment_page',
        success: true,
        data: {
          orderId: orderResponse.orderId,
          paymentUrl: orderResponse.paymentUrl
        }
      });
      
      // Redirect to payment URL
      window.location.href = orderResponse.paymentUrl;
      
      // Return true to indicate successful redirect
      return true;
    } catch (error) {
      console.error('Failed to open payment page:', error);
      
      // Log checkout error
      logPaymentDebug({
        action: 'payment_redirect_error',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      toast.error('Failed to initialize payment. Please try again later.');
      return false;
    }
  }, [createOrderMutation, logPaymentDebug]);
  
  // Get invoice for a payment
  const getInvoice = useCallback(async (paymentId: string): Promise<string | null> => {
    try {
      const response = await subscriptionApi.getInvoice(paymentId);
      return response.invoiceUrl;
    } catch (error) {
      toast.error('Failed to get invoice. Please try again later.');
      return null;
    }
  }, []);
  
  // Save GST details
  const saveGSTDetails = useCallback((gstDetails: GSTDetails): Promise<boolean> => {
    return new Promise((resolve) => {
      saveGSTDetailsMutation.mutate(gstDetails, {
        onSuccess: () => resolve(true),
        onError: () => resolve(false)
      });
    });
  }, [saveGSTDetailsMutation]);
  
  // Get styled badge class for plan
  const getPlanBadgeClass = useCallback((planCode: string): string => {
    switch (planCode.toLowerCase()) {
      case SubscriptionTier.ENTERPRISE_PRO:
        return 'plan-badge-enterprise-pro';
      case SubscriptionTier.ENTERPRISE:
        return 'plan-badge-enterprise';
      case SubscriptionTier.GROWTH:
        return 'plan-badge-growth';
      case SubscriptionTier.FREE:
      default:
        return 'plan-badge-free';
    }
  }, []);
  
  // Check if a subscription has unlimited passes
  const hasUnlimitedPasses = useCallback((subscription: UserSubscription | undefined): boolean => {
    if (!subscription) return false;
    
    return subscription.isUnlimited || 
           subscription.remainingPasses >= UNLIMITED_PASSES_VALUE || 
           subscription.totalPasses >= UNLIMITED_PASSES_VALUE;
  }, []);
  
  // Clear payment debug data (utility function)
  const clearPaymentDebugData = useCallback(() => {
    try {
      localStorage.removeItem('speedmeet_payment_debug');
    } catch (e) {
      // Ignore localStorage errors
    }
  }, []);
  
  // Function to get and export all payment debug data
  const exportPaymentDebugData = useCallback((): string => {
    try {
      const debugData = localStorage.getItem('speedmeet_payment_debug');
      if (!debugData) {
        return 'No payment debug data available';
      }
      
      // Return formatted JSON string
      return JSON.stringify(JSON.parse(debugData), null, 2);
    } catch (e) {
      return `Error retrieving debug data: ${e instanceof Error ? e.message : String(e)}`;
    }
  }, []);
  
  return {
    // Data
    plans,
    subscriptions,
    activeSubscription,
    activeSubscriptionId,
    payments,
    
    // Loading states
    isLoadingPlans,
    isLoadingSubscriptions,
    isLoadingActiveSubscription,
    isLoadingPayments,
    isSettingActive: setActiveMutation.isPending,
    isCreatingOrder: createOrderMutation.isPending,
    isVerifyingPayment: verifyPaymentMutation.isPending,
    isSavingGSTDetails: saveGSTDetailsMutation.isPending,
    
    // Error states
    plansError,
    subscriptionsError,
    activeSubscriptionError,
    paymentsError,
    
    // Actions
    setActiveSubscription,
    openRazorpayCheckout,
    getInvoice,
    saveGSTDetails,
    clearPaymentDebugData,
    exportPaymentDebugData,
    
    // Utility functions
    getPlanByCode,
    formatCurrency,
    formatDate,
    getRemainingDays,
    getPlanBadgeClass,
    hasUnlimitedPasses,
    
    // New utility functions for GST-free pricing
    calculatePreGSTPrice,
    formatCurrencyWithoutGST,
    calculatePricePerPassWithoutGST,
    
    // Refetch functions
    refetchSubscriptions,
    refetchActiveSubscription,
    refetchPayments
  };
};

export default useSubscription;
